import axios from 'axios'
import localforage from "localforage";
import * as config from '../config/config';
import {isOnline} from "./online";

// Device settings whose value is held in the store and persisted to localforage
// already. Each descriptor knows the endpoint to push it to and how to build the
// request body from the CURRENT store state - we push state, never a recorded
// request payload.
const SETTINGS = {
  activity_order:  {endpoint: '/device/activity-order', body: s => ({order: s.activityOrder})},
  rows_order:      {endpoint: '/device/rows-order',     body: s => ({order: s.rowsOrder})},
  teams_order:     {endpoint: '/device/teams-order',    body: s => ({order: s.teamsOrder})},
  rows_as_buttons: {endpoint: '/device/rows-as-buttons', body: s => ({asButtons: s.rowsAsButtons})},
  teams_as_buttons:{endpoint: '/device/teams-as-buttons', body: s => ({asButtons: s.teamsAsButtons})},
  pin_visible:     {endpoint: '/device/settings',       body: s => ({pin_visible: s.pinVisible})},
  dark_mode:       {endpoint: '/device/settings',       body: s => ({dark_mode: s.darkMode})},
};

// Set of setting keys that changed locally and have not reached the server yet.
// Only the keys are stored (which state is dirty) - the values come from the
// store on flush. Survives app restarts so an offline change is not lost.
const STORAGE_KEY = 'pendingDeviceSettings';

const readDirty = () => localforage.getItem(STORAGE_KEY).then(dirty => dirty || {});

// Flag a setting as having an unsynced local change.
export const markPendingDeviceSetting = function (key) {
  return readDirty().then(dirty => {
    dirty[key] = true;
    return localforage.setItem(STORAGE_KEY, dirty);
  });
};

// Clear the flag once the current value is on the server (or no longer dirty).
export const clearPendingDeviceSetting = function (key) {
  return readDirty().then(dirty => {
    if (!dirty[key]) return dirty;
    delete dirty[key];
    return localforage.setItem(STORAGE_KEY, dirty);
  });
};

export const hasPendingDeviceSettings = function () {
  return readDirty().then(dirty => Object.keys(dirty).length > 0);
};

// Push the current value of every dirty setting to the server before new data is
// pulled. Each flag is cleared only after its request succeeds, so a failure
// leaves it dirty for the next attempt. Resolves with how many were flushed and
// how many remain dirty.
export const flushPendingDeviceSettings = function (state) {
  if (!isOnline()) return Promise.resolve({flushed: 0, remaining: -1});

  return readDirty().then(dirty => {
    const keys = Object.keys(dirty).filter(key => SETTINGS[key]);
    if (!keys.length) return {flushed: 0, remaining: 0};

    const reqConfig = {headers: {'Authorization': 'Bearer ' + state.token}};
    let flushed = 0;
    let stopped = false;

    return keys.reduce((chain, key) => chain.then(() => {
      if (stopped) return;

      const setting = SETTINGS[key];
      return axios.put(config.api.current + setting.endpoint, setting.body(state), reqConfig)
        .then(() => clearPendingDeviceSetting(key).then(() => { flushed++; }))
        .catch(() => { stopped = true; });
    }), Promise.resolve()).then(() => ({flushed: flushed, remaining: keys.length - flushed}));
  });
};